import { Directive, Input, OnChanges, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { PermissionsService } from './permissions.service';

// Strukturalna dyrektywa do ukrywania czegokolwiek na podstawie uprawnień -
// zakładka mat-tab, przycisk, <ng-container> wokół inputu, link menu.
//
// Użycie:
//   <a *appHasPermission="'admin.settings.companies'">Firmy</a>
//   <button *appHasPermission="'admin.settings.general'; level: 'write'">Zapisz</button>
//
// Przyjmuje też TABLICĘ kluczy - element pokazuje się, gdy user ma dostęp do
// KTOREGOKOLWIEK z nich. Do tego stworzona: przycisk-trigger menu (np. "Admin",
// "Baza klientów"), ktory sam nie jest osobnym zasobem, tylko kontenerem dla
// kilku linkow ponizej - bez tego user z dostepem do zera dzieci widzialby
// przycisk otwierajacy pusty dropdown ("po co pokazywac menu, skoro nie ma do
// niego uprawnien"). Ten sam wzorzec ma dzialac dla KAZDEGO takiego przycisku
// w miare dokladania kolejnych sekcji menu, nie tylko dzisiejszych dwoch:
//   <button *appHasPermission="['admin.profiles', 'admin.settings']">Admin</button>
//
// Domyślny poziom to 'read' (przepuszcza zarówno 'read' jak i 'write').
//
// Subskrybuje PermissionsService.changes (nie tylko ngOnChanges na własnym
// @Input) - komponenty montowane natychmiast przy starcie appki (np. menu)
// renderują się ZANIM PermissionsService.refresh() (async, wołane w
// AppComponent.ngOnInit) zdąży się skończyć. Bez subskrypcji dyrektywa
// oceniłaby pustą mapę raz i nigdy by się nie przeliczyła po dociągnięciu
// danych.
@Directive({
  selector: '[appHasPermission]',
})
export class HasPermissionDirective implements OnInit, OnChanges, OnDestroy {
  @Input('appHasPermission') resourceKey: string | string[];
  @Input('appHasPermissionLevel') level: 'read' | 'write' = 'read';

  private rendered = false;
  private sub: Subscription | null = null;

  constructor(
    private readonly templateRef: TemplateRef<unknown>,
    private readonly viewContainer: ViewContainerRef,
    private readonly permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.sub = this.permissionsService.changes.subscribe(() => this.updateView());
  }

  ngOnChanges(): void {
    this.updateView();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateView(): void {
    const keys = Array.isArray(this.resourceKey) ? this.resourceKey : [this.resourceKey];
    const allowed = keys.some(key => !!key && this.permissionsService.has(key, this.level));

    if (allowed && !this.rendered) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.rendered = true;
    } else if (!allowed && this.rendered) {
      this.viewContainer.clear();
      this.rendered = false;
    }
  }
}
